/**
 * Quick Add: post one staged file to a course. Recordings go straight to S3
 * (same path as Manage content), slides/readings upload through the server.
 */
import apiService from '../../../services/api';
import { normalizeContentLink } from '../../../pages/admin/content/addContentModal/addContentHelpers';

const MULTIPART_THRESHOLD = 100 * 1024 * 1024; // 100 MB, same as Manage content

const idKey = () => `content-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export class QuickAddError extends Error {}

function friendly(error) {
  if (error?.status === 409 && error?.data?.code === 'RECORDING_EXISTS') {
    return new QuickAddError('There’s already a recording for today. Replace it from Manage content.');
  }
  return error instanceof Error ? error : new Error(String(error || 'Upload failed'));
}

async function uploadRecording({ course, item, recordingDate, suffix, onProgress, signal }) {
  const file = item.file;
  if (file.size > MULTIPART_THRESHOLD) {
    const result = await apiService.uploadCourseRecordingMultipart(course.id, file, recordingDate, onProgress, {
      signal, ...(suffix ? { fileNameSuffix: suffix } : {}),
    });
    return { key: result.key, url: result.objectUrl };
  }
  const presign = await apiService.createCourseRecordingS3Upload(course.id, file, recordingDate, undefined, false, suffix);
  if (!presign?.success) throw new Error(presign?.message || 'Couldn’t start the upload');
  await apiService.uploadToS3(presign.uploadUrl, file, presign.headers, onProgress, signal);
  return { key: presign.key, url: presign.objectUrl };
}

/**
 * @param {object} p
 * @param {object} p.course        course row ({ id })
 * @param {object} p.item          staged row from stageFile()
 * @param {string} p.recordingDate 'YYYY-MM-DD' (Chicago) for recordings
 * @param {string|null} p.recordingDateUtc ISO UTC of that date's Chicago midnight
 * @param {string|null} p.publishAt ISO UTC, or null for now
 * @param {boolean} p.sendEmail
 * @param {string} [p.suffix]       S3 file-name suffix when posting several videos
 */
export async function postQuickAddItem({
  course, item, recordingDate, recordingDateUtc, publishAt, sendEmail, suffix, onProgress, signal,
}) {
  try {
    let response;
    if (item.type === 'recording') {
      const uploaded = await uploadRecording({ course, item, recordingDate, suffix, onProgress, signal });
      response = await apiService.addCourseContent(course.id, {
        type: 'recording',
        title: item.title,
        priority: 'medium',
        week: item.week || null,
        recordingDate: recordingDateUtc,
        url: uploaded.url,
        s3Key: uploaded.key,
        fileName: item.file.name,
        fileSize: item.file.size,
        sendEmail,
        ...(publishAt ? { publishAt } : {}),
        idempotencyKey: idKey(),
      });
    } else {
      const fd = new FormData();
      // `type` must come before `file`: the server picks allowed file types from it.
      fd.append('type', item.type);
      fd.append('title', item.title);
      fd.append('priority', 'medium');
      if (item.week && item.type !== 'syllabus') fd.append('week', String(item.week));
      // The optional link: a reading keeps its links in `urls`, slides and the syllabus in `url`.
      const link = normalizeContentLink(item.url);
      if (link) fd.append(item.type === 'reading' ? 'urls' : 'url', item.type === 'reading' ? JSON.stringify([{ url: link, label: '' }]) : link);
      fd.append('sendEmail', String(sendEmail));
      if (publishAt) fd.append('publishAt', publishAt);
      fd.append('idempotencyKey', idKey());
      if (item.file) fd.append('file', item.file);
      fd.__onUploadProgress = onProgress;
      response = await apiService.addCourseContent(course.id, fd);
    }
    if (!response?.success) throw new Error(response?.message || 'Couldn’t post this file');
    return response;
  } catch (error) {
    throw friendly(error);
  }
}
